import React, { useEffect, useState, useRef } from 'react';
import { XIcon, MailIcon, PhoneIcon, MusicIcon, CheckCircleIcon, PencilIcon, GlobeIcon, HomeIcon, ClockIcon, MapPinIcon, UserIcon, SaveIcon, Trash2Icon, AlertCircleIcon } from 'lucide-react';
import { formatPhoneNumber, normalizePhoneNumber, getFormattedCursorPosition } from '../../utils/phoneUtils';
import { validateEmail, validatePhone } from '../../utils/validationUtils';
import { useAuth } from '../../contexts/AuthContext';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../../firebase';
import { deleteStudentAccount } from '../../services/studentService';
import toast from 'react-hot-toast';
interface Student {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  instrument?: string;
  instruments?: string[];
  notes?: string;
  avatar?: string;
  profilePicture?: string;
  status?: string;
  lessonType?: 'In-person' | 'Online' | 'Hybrid';
  timeZone?: string;
  address?: {
    street?: string;
    apt?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  guardian?: {
    name?: string;
    email?: string;
    phone?: string;
    preferredContact?: string;
    isEmergencyContact?: boolean;
  };
  upcomingLessonsCount?: number;
  pendingTasksCount?: number;
  lastReportDate?: string;
}
interface ViewStudentProfilePanelProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  darkMode: boolean;
  onEdit?: (studentId: string, updatedData: any) => void;
  onViewFullProfile?: (studentId: string) => void;
  onStudentDeleted?: () => void;
}
export const ViewStudentProfilePanel: React.FC<ViewStudentProfilePanelProps> = ({
  isOpen,
  onClose,
  student,
  darkMode,
  onEdit,
  onViewFullProfile,
  onStudentDeleted
}) => {
  const {
    currentUser
  } = useAuth();
  // Single edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // NEW: Add displayStudent state for optimistic updates
  const [displayStudent, setDisplayStudent] = useState<Student | null>(null);
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0
  });
  // Edit state for all fields
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [editedInstrument, setEditedInstrument] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedLessonType, setEditedLessonType] = useState<'In-person' | 'Online' | 'Hybrid' | ''>('');
  const [editedTimeZone, setEditedTimeZone] = useState('');
  const [editedNotes, setEditedNotes] = useState('');
  // Address edit state
  const [editedAddressStreet, setEditedAddressStreet] = useState('');
  const [editedAddressApt, setEditedAddressApt] = useState('');
  const [editedAddressCity, setEditedAddressCity] = useState('');
  const [editedAddressState, setEditedAddressState] = useState('');
  const [editedAddressZip, setEditedAddressZip] = useState('');
  // Guardian edit state
  const [editedGuardianName, setEditedGuardianName] = useState('');
  const [editedGuardianEmail, setEditedGuardianEmail] = useState('');
  const [editedGuardianPhone, setEditedGuardianPhone] = useState('');
  const [editedGuardianPreferredContact, setEditedGuardianPreferredContact] = useState('');
  const [editedGuardianIsEmergencyContact, setEditedGuardianIsEmergencyContact] = useState(false);
  // Refs for phone inputs to manage cursor position
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const guardianPhoneInputRef = useRef<HTMLInputElement>(null);
  // Delete student states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  // NEW: Validation error states
  const [validationErrors, setValidationErrors] = useState({
    email: null as string | null,
    phone: null as string | null,
    guardianEmail: null as string | null,
    guardianPhone: null as string | null
  });
  // NEW: Sync displayStudent with student prop changes
  useEffect(() => {
    setDisplayStudent(student);
  }, [student]);
  useEffect(() => {
    if (isOpen && student) {
      // Initialize all edit fields with current student data
      setEditedFirstName(student.firstName || '');
      setEditedLastName(student.lastName || '');
      setEditedInstrument(student.instrument || (student.instruments && student.instruments.length > 0 ? student.instruments[0] : ''));
      setEditedEmail(student.email || '');
      // Format phone for display
      setEditedPhone(formatPhoneNumber(student.phone || ''));
      setEditedLessonType(student.lessonType || '');
      setEditedTimeZone(student.timeZone || '');
      setEditedNotes(student.notes || '');
      // Initialize address fields
      setEditedAddressStreet(student.address?.street || '');
      setEditedAddressApt(student.address?.apt || '');
      setEditedAddressCity(student.address?.city || '');
      setEditedAddressState(student.address?.state || '');
      setEditedAddressZip(student.address?.zip || '');
      // Initialize guardian fields - format guardian phone for display
      setEditedGuardianName(student.guardian?.name || '');
      setEditedGuardianEmail(student.guardian?.email || '');
      setEditedGuardianPhone(formatPhoneNumber(student.guardian?.phone || ''));
      setEditedGuardianPreferredContact(student.guardian?.preferredContact || '');
      setEditedGuardianIsEmergencyContact(student.guardian?.isEmergencyContact || false);
      // Reset edit mode and success message
      setIsEditMode(false);
      setSaveSuccess(false);
      // Reset position when panel opens
      setPosition({
        x: 0,
        y: 0
      });
      setIsDragging(false);
      // Reset delete modal state
      setIsDeleteModalOpen(false);
      setDeletePassword('');
      setPasswordError('');
      // Reset validation errors
      setValidationErrors({
        email: null,
        phone: null,
        guardianEmail: null,
        guardianPhone: null
      });
    }
  }, [isOpen, student]);
  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging from the header, not from buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y
    };
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - dragRef.current.startX;
      const deltaY = e.clientY - dragRef.current.startY;
      setPosition({
        x: dragRef.current.initialX + deltaX,
        y: dragRef.current.initialY + deltaY
      });
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  // Special handler for student phone input with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const oldValue = editedPhone;
    const oldCursor = input.selectionStart || 0;
    const rawValue = input.value;
    // Format the phone number
    const formatted = formatPhoneNumber(rawValue);
    // Update state
    setEditedPhone(formatted);
    // Clear validation error when user types
    if (validationErrors.phone) {
      setValidationErrors(prev => ({
        ...prev,
        phone: null
      }));
    }
    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      if (phoneInputRef.current) {
        const newCursor = getFormattedCursorPosition(oldValue, formatted, oldCursor);
        phoneInputRef.current.setSelectionRange(newCursor, newCursor);
      }
    });
  };
  // Special handler for guardian phone input with formatting
  const handleGuardianPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const oldValue = editedGuardianPhone;
    const oldCursor = input.selectionStart || 0;
    const rawValue = input.value;
    // Format the phone number
    const formatted = formatPhoneNumber(rawValue);
    // Update state
    setEditedGuardianPhone(formatted);
    // Clear validation error when user types
    if (validationErrors.guardianPhone) {
      setValidationErrors(prev => ({
        ...prev,
        guardianPhone: null
      }));
    }
    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      if (guardianPhoneInputRef.current) {
        const newCursor = getFormattedCursorPosition(oldValue, formatted, oldCursor);
        guardianPhoneInputRef.current.setSelectionRange(newCursor, newCursor);
      }
    });
  };
  // NEW: Validation handlers for email fields
  const handleEmailBlur = () => {
    const validation = validateEmail(editedEmail);
    setValidationErrors(prev => ({
      ...prev,
      email: validation.error
    }));
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedEmail(e.target.value);
    // Clear error when user types
    if (validationErrors.email) {
      setValidationErrors(prev => ({
        ...prev,
        email: null
      }));
    }
  };
  const handleGuardianEmailBlur = () => {
    const validation = validateEmail(editedGuardianEmail);
    setValidationErrors(prev => ({
      ...prev,
      guardianEmail: validation.error
    }));
  };
  const handleGuardianEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedGuardianEmail(e.target.value);
    // Clear error when user types
    if (validationErrors.guardianEmail) {
      setValidationErrors(prev => ({
        ...prev,
        guardianEmail: null
      }));
    }
  };
  // NEW: Validation handlers for phone fields
  const handlePhoneBlur = () => {
    const validation = validatePhone(editedPhone);
    setValidationErrors(prev => ({
      ...prev,
      phone: validation.error
    }));
  };
  const handleGuardianPhoneBlur = () => {
    const validation = validatePhone(editedGuardianPhone);
    setValidationErrors(prev => ({
      ...prev,
      guardianPhone: validation.error
    }));
  };
  const handleEnterEditMode = () => {
    setIsEditMode(true);
    setSaveSuccess(false);
  };
  const handleSaveAll = () => {
    if (onEdit && student) {
      // Validate names
      const trimmedFirstName = editedFirstName.trim();
      const trimmedLastName = editedLastName.trim();
      if (!trimmedFirstName || !trimmedLastName) {
        toast.error('First name and last name are required.');
        return;
      }
      // Validate name pattern (letters, spaces, hyphens, apostrophes)
      const namePattern = /^[a-zA-Z\s'-]+$/;
      if (!namePattern.test(trimmedFirstName) || !namePattern.test(trimmedLastName)) {
        toast.error('Names can only contain letters, spaces, hyphens, and apostrophes.');
        return;
      }
      // NEW: Validate email and phone fields before saving
      const emailValidation = validateEmail(editedEmail);
      const phoneValidation = validatePhone(editedPhone);
      const guardianEmailValidation = validateEmail(editedGuardianEmail);
      const guardianPhoneValidation = validatePhone(editedGuardianPhone);
      // Update validation errors state
      setValidationErrors({
        email: emailValidation.error,
        phone: phoneValidation.error,
        guardianEmail: guardianEmailValidation.error,
        guardianPhone: guardianPhoneValidation.error
      });
      // Check if any validation failed
      if (!emailValidation.isValid || !phoneValidation.isValid || !guardianEmailValidation.isValid || !guardianPhoneValidation.isValid) {
        toast.error('Please fix validation errors before saving.');
        return;
      }
      // Create full name for display
      const fullName = `${trimmedFirstName} ${trimmedLastName}`;
      // Normalize phone numbers to digits-only for storage
      const normalizedPhone = normalizePhoneNumber(editedPhone);
      const normalizedGuardianPhone = normalizePhoneNumber(editedGuardianPhone);
      // Prepare update payload with all edited fields
      const updatePayload = {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        name: fullName,
        instrument: editedInstrument.trim() || undefined,
        email: editedEmail,
        phone: normalizedPhone,
        lessonType: editedLessonType || undefined,
        timeZone: editedTimeZone || undefined,
        notes: editedNotes,
        address: {
          street: editedAddressStreet.trim() || undefined,
          apt: editedAddressApt.trim() || undefined,
          city: editedAddressCity.trim() || undefined,
          state: editedAddressState.trim() || undefined,
          zip: editedAddressZip.trim() || undefined
        },
        guardian: {
          name: editedGuardianName.trim() || undefined,
          email: editedGuardianEmail.trim() || undefined,
          phone: normalizedGuardianPhone,
          preferredContact: editedGuardianPreferredContact || undefined,
          isEmergencyContact: editedGuardianIsEmergencyContact
        }
      };
      console.log('ViewStudentProfilePanel: Saving all changes:', updatePayload);
      // Call parent's onEdit handler
      onEdit(student.id, updatePayload);
      // ✅ NEW: Optimistically update displayStudent immediately
      const optimisticStudent: Student = {
        ...student,
        ...updatePayload
      };
      setDisplayStudent(optimisticStudent);
      setIsEditMode(false);
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }
  };
  const handleCancelEdit = () => {
    if (student) {
      // Reset all fields to original values
      setEditedFirstName(student.firstName || '');
      setEditedLastName(student.lastName || '');
      setEditedInstrument(student.instrument || (student.instruments && student.instruments.length > 0 ? student.instruments[0] : ''));
      setEditedEmail(student.email || '');
      // Format phone for display when canceling
      setEditedPhone(formatPhoneNumber(student.phone || ''));
      setEditedLessonType(student.lessonType || '');
      setEditedTimeZone(student.timeZone || '');
      setEditedNotes(student.notes || '');
      // Reset address fields
      setEditedAddressStreet(student.address?.street || '');
      setEditedAddressApt(student.address?.apt || '');
      setEditedAddressCity(student.address?.city || '');
      setEditedAddressState(student.address?.state || '');
      setEditedAddressZip(student.address?.zip || '');
      // Reset guardian fields - format guardian phone for display
      setEditedGuardianName(student.guardian?.name || '');
      setEditedGuardianEmail(student.guardian?.email || '');
      setEditedGuardianPhone(formatPhoneNumber(student.guardian?.phone || ''));
      setEditedGuardianPreferredContact(student.guardian?.preferredContact || '');
      setEditedGuardianIsEmergencyContact(student.guardian?.isEmergencyContact || false);
    }
    setIsEditMode(false);
    setSaveSuccess(false);
  };
  const handleMessage = () => {
    if (onViewFullProfile && student) {
      onViewFullProfile(student.id);
      onClose();
    }
  };
  const handleAssignTask = () => {
    if (onAssignTask && student) {
      onAssignTask(student.id);
      onClose();
    }
  };
  const handleScheduleLesson = () => {
    if (onScheduleLesson && student) {
      onScheduleLesson(student.id);
      onClose();
    }
  };
  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
    setPasswordError('');
    setDeletePassword('');
  };
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletePassword('');
    setPasswordError('');
  };
  const handleDeleteConfirm = async () => {
    if (!currentUser || !student) {
      setPasswordError('Unable to authenticate. Please try again.');
      return;
    }
    // Validate password is not empty
    if (!deletePassword.trim()) {
      setPasswordError('Password is required to confirm deletion.');
      return;
    }
    setIsDeleting(true);
    setPasswordError('');
    try {
      // Step 1: Reauthenticate the user with their password
      const credential = EmailAuthProvider.credential(currentUser.email!, deletePassword);
      try {
        await reauthenticateWithCredential(auth.currentUser!, credential);
      } catch (authError: any) {
        console.error('Authentication error:', authError);
        // Handle specific authentication errors
        if (authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
          setPasswordError('Incorrect password. Please try again.');
        } else if (authError.code === 'auth/too-many-requests') {
          setPasswordError('Too many attempts. Please try again later.');
        } else {
          setPasswordError('Authentication failed. Please try again.');
        }
        setIsDeleting(false);
        return;
      }
      // Step 2: If authentication succeeds, delete the student
      const studentId = student.id;
      await deleteStudentAccount(studentId, currentUser.uid);
      // Step 3: Success - close modals and show success message
      setIsDeleteModalOpen(false);
      setDeletePassword('');
      // Show success toast
      toast.success('Student profile deleted successfully.', {
        duration: 4000,
        icon: '✅'
      });
      // Step 4: Close the profile panel
      onClose();
      // Step 5: Notify parent component to refresh student list
      if (onStudentDeleted) {
        onStudentDeleted();
      }
      // Also dispatch a custom event for other components that might be listening
      const deleteEvent = new CustomEvent('studentDeleted', {
        detail: {
          studentId
        }
      });
      window.dispatchEvent(deleteEvent);
    } catch (error: any) {
      console.error('Error deleting student:', error);
      // Handle specific deletion errors
      let errorMessage = 'Failed to delete student. Please try again.';
      if (error.message) {
        if (error.message.includes('not found')) {
          errorMessage = 'Student not found. They may have already been deleted.';
        } else if (error.message.includes('permission')) {
          errorMessage = 'Permission denied. Please check your account permissions.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      }
      toast.error(errorMessage, {
        duration: 5000,
        icon: '❌'
      });
      setIsDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };
  // Handler for Enter key in password field
  const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isDeleting && deletePassword.trim()) {
      e.preventDefault();
      handleDeleteConfirm();
    }
  };
  if (!isOpen || !displayStudent) return null;
  // Get full name with safe fallback
  const fullName = displayStudent.name || (displayStudent.firstName && displayStudent.lastName ? `${displayStudent.firstName} ${displayStudent.lastName}` : displayStudent.firstName || displayStudent.lastName || 'Unnamed Student');
  // Get instrument - prefer singular, fallback to first of array, or empty
  const displayInstrument = displayStudent.instrument || (displayStudent.instruments && displayStudent.instruments.length > 0 ? displayStudent.instruments[0] : '');
  // Get status badge styling
  const getStatusBadgeClass = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return darkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-500/10 text-green-600 border border-green-500/30';
      case 'trial':
        return darkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-500/10 text-blue-600 border border-blue-500/30';
      case 'inactive':
      case 'archived':
        return darkMode ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'bg-gray-500/10 text-gray-600 border border-gray-500/30';
      default:
        return darkMode ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' : 'bg-gray-500/10 text-gray-600 border border-gray-500/30';
    }
  };
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-2050">
      {/* Add style tag to fix autocomplete background */}
      <style data-id="element-2051">{`
        /* Prevent autocomplete from changing background color in dark mode */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${darkMode ? 'rgba(10, 17, 40, 0.5)' : 'white'} inset !important;
          -webkit-text-fill-color: ${darkMode ? 'white' : '#111827'} !important;
          caret-color: ${darkMode ? 'white' : '#111827'} !important;
        }
        /* Firefox autocomplete fix */
        input:-moz-autofill,
        input:-moz-autofill:hover,
        input:-moz-autofill:focus {
          background-color: ${darkMode ? 'rgba(10, 17, 40, 0.5)' : 'white'} !important;
          color: ${darkMode ? 'white' : '#111827'} !important;
        }
      `}</style>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={onClose} data-id="element-2052" />
      {/* Centered Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none" data-id="element-2053">
        <div className={`w-full max-w-xl transform transition-all duration-300 ease-in-out pointer-events-auto ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`} style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        transition: isDragging ? 'none' : 'transform 0.3s ease-in-out'
      }} data-id="element-2054">
          <div className={`flex flex-col max-h-[90vh] ${darkMode ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50' : 'bg-white border border-gray-200'} shadow-xl rounded-2xl overflow-hidden`} data-id="element-2055">
            {/* Header - Draggable Area */}
            <div className={`px-6 py-5 flex items-center justify-between border-b ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} select-none`} onMouseDown={handleMouseDown} data-id="element-2056">
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2057">
                Student Profile
              </h2>
              <button onClick={onClose} className={`rounded-lg p-1.5 transition-colors cursor-pointer ${darkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} data-id="element-2058">
                <XIcon className="h-5 w-5" data-id="element-2059" />
              </button>
            </div>
            {/* Scrollable Content */}
            <div className={`flex-1 overflow-y-auto px-6 py-6 space-y-6 student-profile-scrollbar ${darkMode ? 'dark-mode' : ''}`} data-id="element-2060">
              {/* Success Message */}
              {saveSuccess && <div className={`p-3 rounded-lg flex items-center gap-2 ${darkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-700 border border-green-200'}`} data-id="element-2061">
                  <CheckCircleIcon className="h-5 w-5" data-id="element-2062" />
                  <span className="text-sm font-medium" data-id="element-2063">
                    Changes saved successfully!
                  </span>
                </div>}
              {/* Name Section with Avatar, Name, Status, and Instrument */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-navy-900/50 border border-navy-700/30' : 'bg-gray-50 border border-gray-200/50'} shadow-sm`} data-id="element-2064">
                <div data-id="element-2065">
                  {/* Name and Instrument */}
                  <div className="flex-1 min-w-0" data-id="element-2066">
                    {isEditMode ? <div className="space-y-3" data-id="element-2067">
                        {/* First Name Input */}
                        <div data-id="element-2068">
                          <label htmlFor="firstName" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2069">
                            First Name
                          </label>
                          <input id="firstName" type="text" value={editedFirstName} onChange={e => setEditedFirstName(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="First Name" data-id="element-2070" />
                        </div>
                        {/* Last Name Input */}
                        <div data-id="element-2071">
                          <label htmlFor="lastName" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2072">
                            Last Name
                          </label>
                          <input id="lastName" type="text" value={editedLastName} onChange={e => setEditedLastName(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="Last Name" data-id="element-2073" />
                        </div>
                        {/* Instrument Text Input */}
                        <div data-id="element-2074">
                          <label htmlFor="instrument" className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2075">
                            Instrument / Lesson
                          </label>
                          <input id="instrument" type="text" value={editedInstrument} onChange={e => setEditedInstrument(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="e.g., Piano, Drums, Music Theory, Music Business" data-id="element-2076" />
                        </div>
                      </div> : <>
                        <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} truncate mb-2`} data-id="element-2077">
                          {fullName}
                        </h3>
                        {/* Instrument pill */}
                        <div className="flex items-center gap-2" data-id="element-2078">
                          {displayInstrument && <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' : 'bg-violet-500/10 text-violet-600 border border-violet-500/30'}`} data-id="element-2079">
                              <MusicIcon className="h-3 w-3 mr-1" data-id="element-2080" />
                              {displayInstrument}
                            </span>}
                        </div>
                      </>}
                  </div>
                </div>
              </div>
              {/* Contact & Basics Section */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-navy-900/50 border border-navy-700/30' : 'bg-gray-50 border border-gray-200/50'} shadow-sm`} data-id="element-2081">
                <div className="mb-4" data-id="element-2082">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2083">
                    Contact & Details
                  </h3>
                </div>
                {isEditMode ? <div className="space-y-4" data-id="element-2084">
                    {/* Email Input */}
                    <div data-id="element-2085">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2086">
                        Email
                      </label>
                      <input type="email" value={editedEmail} onChange={handleEmailChange} onBlur={handleEmailBlur} className={`w-full px-3 py-2 rounded-lg border ${validationErrors.email ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="student@example.com" data-id="element-2087" />
                      {validationErrors.email && <p className="mt-1 text-xs text-red-500 flex items-center gap-1" data-id="element-2088">
                          <AlertCircleIcon className="h-3 w-3" data-id="element-2089" />
                          {validationErrors.email}
                        </p>}
                    </div>
                    {/* Phone Input with formatting */}
                    <div data-id="element-2090">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2091">
                        Phone
                      </label>
                      <input ref={phoneInputRef} type="tel" value={editedPhone} onChange={handlePhoneChange} onBlur={handlePhoneBlur} placeholder="(555) 123-4567" maxLength={14} className={`w-full px-3 py-2 rounded-lg border ${validationErrors.phone ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2092" />
                      {validationErrors.phone && <p className="mt-1 text-xs text-red-500 flex items-center gap-1" data-id="element-2093">
                          <AlertCircleIcon className="h-3 w-3" data-id="element-2094" />
                          {validationErrors.phone}
                        </p>}
                    </div>
                    {/* Lesson Type Select */}
                    <div data-id="element-2095">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2096">
                        Lesson Type
                      </label>
                      <select value={editedLessonType} onChange={e => {
                    const value = e.target.value as 'In-person' | 'Online' | 'Hybrid' | '';
                    setEditedLessonType(value);
                  }} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2097">
                        <option value="" data-id="element-2098">Select lesson type</option>
                        <option value="In-person" data-id="element-2099">In-person</option>
                        <option value="Online" data-id="element-2100">Online</option>
                        <option value="Hybrid" data-id="element-2101">Hybrid</option>
                      </select>
                    </div>
                    {/* Time Zone Select */}
                    <div data-id="element-2102">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2103">
                        Time Zone
                      </label>
                      <select value={editedTimeZone} onChange={e => setEditedTimeZone(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2104">
                        <option value="" data-id="element-2105">Select time zone</option>
                        <option value="Eastern Time (ET)" data-id="element-2106">
                          Eastern Time (ET)
                        </option>
                        <option value="Central Time (CT)" data-id="element-2107">
                          Central Time (CT)
                        </option>
                        <option value="Mountain Time (MT)" data-id="element-2108">
                          Mountain Time (MT)
                        </option>
                        <option value="Pacific Time (PT)" data-id="element-2109">
                          Pacific Time (PT)
                        </option>
                        <option value="Alaska Time (AKT)" data-id="element-2110">
                          Alaska Time (AKT)
                        </option>
                        <option value="Hawaii-Aleutian Time (HST)" data-id="element-2111">
                          Hawaii-Aleutian Time (HST)
                        </option>
                      </select>
                    </div>
                  </div> : <div className="grid grid-cols-2 gap-4" data-id="element-2112">
                    {/* Email */}
                    <div className="space-y-1" data-id="element-2113">
                      <div className="flex items-center gap-2" data-id="element-2114">
                        <MailIcon className="h-4 w-4 text-blue-500" data-id="element-2115" />
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2116">
                          Email
                        </span>
                      </div>
                      <p className={`text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2117">
                        {displayStudent.email || 'Not provided'}
                      </p>
                    </div>
                    {/* Phone - display formatted */}
                    <div className="space-y-1" data-id="element-2118">
                      <div className="flex items-center gap-2" data-id="element-2119">
                        <PhoneIcon className="h-4 w-4 text-green-500" data-id="element-2120" />
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2121">
                          Phone
                        </span>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2122">
                        {displayStudent.phone ? formatPhoneNumber(displayStudent.phone) : 'Not provided'}
                      </p>
                    </div>
                    {/* Lesson Type */}
                    <div className="space-y-1" data-id="element-2123">
                      <div className="flex items-center gap-2" data-id="element-2124">
                        {displayStudent.lessonType === 'Online' ? <GlobeIcon className="h-4 w-4 text-yellow-500" data-id="element-2125" /> : <HomeIcon className="h-4 w-4 text-yellow-500" data-id="element-2126" />}
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2127">
                          Lesson Type
                        </span>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2128">
                        {displayStudent.lessonType || 'Not specified'}
                      </p>
                    </div>
                    {/* Time Zone */}
                    <div className="space-y-1" data-id="element-2129">
                      <div className="flex items-center gap-2" data-id="element-2130">
                        <ClockIcon className="h-4 w-4 text-orange-500" data-id="element-2131" />
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2132">
                          Time Zone
                        </span>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2133">
                        {displayStudent.timeZone || 'Not specified'}
                      </p>
                    </div>
                  </div>}
              </div>
              {/* Address Section */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-navy-900/50 border border-navy-700/30' : 'bg-gray-50 border border-gray-200/50'} shadow-sm`} data-id="element-2134">
                <div className="mb-4" data-id="element-2135">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2136">
                    Address
                  </h3>
                </div>
                {isEditMode ? <div className="space-y-4" data-id="element-2137">
                    {/* Street Address */}
                    <div data-id="element-2138">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2139">
                        Street Address
                      </label>
                      <input type="text" value={editedAddressStreet} onChange={e => setEditedAddressStreet(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2140" />
                    </div>
                    {/* Apartment/Unit */}
                    <div data-id="element-2141">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2142">
                        Apartment/Unit (Optional)
                      </label>
                      <input type="text" value={editedAddressApt} onChange={e => setEditedAddressApt(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2143" />
                    </div>
                    {/* City, State, ZIP in a grid */}
                    <div className="grid grid-cols-2 gap-3" data-id="element-2144">
                      {/* City */}
                      <div className="col-span-2" data-id="element-2145">
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2146">
                          City
                        </label>
                        <input type="text" value={editedAddressCity} onChange={e => setEditedAddressCity(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2147" />
                      </div>
                      {/* State */}
                      <div data-id="element-2148">
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2149">
                          State
                        </label>
                        <input type="text" value={editedAddressState} onChange={e => setEditedAddressState(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2150" />
                      </div>
                      {/* ZIP */}
                      <div data-id="element-2151">
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2152">
                          ZIP Code
                        </label>
                        <input type="text" value={editedAddressZip} onChange={e => setEditedAddressZip(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2153" />
                      </div>
                    </div>
                  </div> : <div data-id="element-2154">
                    {displayStudent.address && (displayStudent.address.street || displayStudent.address.city || displayStudent.address.state) ? <div className="space-y-1" data-id="element-2155">
                        <div className="flex items-center gap-2 mb-2" data-id="element-2156">
                          <MapPinIcon className="h-4 w-4 text-red-500" data-id="element-2157" />
                          <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2158">
                            Location
                          </span>
                        </div>
                        {displayStudent.address.street && <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2159">
                            {displayStudent.address.street}
                            {displayStudent.address.apt && `, ${displayStudent.address.apt}`}
                          </p>}
                        {(displayStudent.address.city || displayStudent.address.state) && <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2160">
                            {displayStudent.address.city}
                            {displayStudent.address.city && displayStudent.address.state && ', '}
                            {displayStudent.address.state}{' '}
                            {displayStudent.address.zip}
                          </p>}
                      </div> : <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2161">
                        No address provided
                      </p>}
                  </div>}
              </div>
              {/* Guardian/Parent Section */}
              <div className={`p-5 rounded-xl ${darkMode ? 'bg-navy-900/50 border border-navy-700/30' : 'bg-gray-50 border border-gray-200/50'} shadow-sm`} data-id="element-2162">
                <div className="mb-4" data-id="element-2163">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2164">
                    Parent / Guardian
                  </h3>
                </div>
                {isEditMode ? <div className="space-y-4" data-id="element-2165">
                    {/* Guardian Name */}
                    <div data-id="element-2166">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2167">
                        Guardian Name
                      </label>
                      <input type="text" value={editedGuardianName} onChange={e => setEditedGuardianName(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="Parent or Guardian Name" data-id="element-2168" />
                    </div>
                    {/* Guardian Email */}
                    <div data-id="element-2169">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2170">
                        Guardian Email
                      </label>
                      <input type="email" value={editedGuardianEmail} onChange={handleGuardianEmailChange} onBlur={handleGuardianEmailBlur} className={`w-full px-3 py-2 rounded-lg border ${validationErrors.guardianEmail ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} placeholder="guardian@example.com" data-id="element-2171" />
                      {validationErrors.guardianEmail && <p className="mt-1 text-xs text-red-500 flex items-center gap-1" data-id="element-2172">
                          <AlertCircleIcon className="h-3 w-3" data-id="element-2173" />
                          {validationErrors.guardianEmail}
                        </p>}
                    </div>
                    {/* Guardian Phone with formatting */}
                    <div data-id="element-2174">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2175">
                        Guardian Phone
                      </label>
                      <input ref={guardianPhoneInputRef} type="tel" value={editedGuardianPhone} onChange={handleGuardianPhoneChange} onBlur={handleGuardianPhoneBlur} placeholder="(555) 987-6543" maxLength={14} className={`w-full px-3 py-2 rounded-lg border ${validationErrors.guardianPhone ? 'border-red-500' : darkMode ? 'border-navy-700/50' : 'border-gray-300'} ${darkMode ? 'bg-navy-900/50 text-white placeholder-gray-500' : 'bg-white text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2176" />
                      {validationErrors.guardianPhone && <p className="mt-1 text-xs text-red-500 flex items-center gap-1" data-id="element-2177">
                          <AlertCircleIcon className="h-3 w-3" data-id="element-2178" />
                          {validationErrors.guardianPhone}
                        </p>}
                    </div>
                    {/* Preferred Contact Method */}
                    <div data-id="element-2179">
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2180">
                        Preferred Contact Method
                      </label>
                      <select value={editedGuardianPreferredContact} onChange={e => setEditedGuardianPreferredContact(e.target.value)} className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} ${darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:ring-deep-pink focus:border-deep-pink'}`} data-id="element-2181">
                        <option value="" data-id="element-2182">Select method</option>
                        <option value="Email" data-id="element-2183">Email</option>
                        <option value="Phone" data-id="element-2184">Phone</option>
                        <option value="Text" data-id="element-2185">Text</option>
                      </select>
                    </div>
                    {/* Emergency Contact Checkbox */}
                    <div className="flex items-center" data-id="element-2186">
                      <input type="checkbox" id="emergencyContact" checked={editedGuardianIsEmergencyContact} onChange={e => setEditedGuardianIsEmergencyContact(e.target.checked)} className={`mr-2 h-4 w-4 rounded ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-emerald-500' : 'bg-white border-gray-300 text-deep-pink'} focus:ring-emerald-500`} data-id="element-2187" />
                      <label htmlFor="emergencyContact" className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2188">
                        Emergency Contact
                      </label>
                    </div>
                  </div> : <div data-id="element-2189">
                    {displayStudent.guardian && displayStudent.guardian.name ? <div className="space-y-3" data-id="element-2190">
                        <div className="flex items-center gap-2 mb-2" data-id="element-2191">
                          <UserIcon className="h-4 w-4 text-purple-500" data-id="element-2192" />
                          <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2193">
                            Guardian Information
                          </span>
                        </div>
                        {/* Guardian Name */}
                        <div data-id="element-2194">
                          <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2195">
                            {displayStudent.guardian.name}
                            {displayStudent.guardian.isEmergencyContact && <span className={`ml-2 text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2196">
                                (Emergency Contact)
                              </span>}
                          </p>
                        </div>
                        {/* Guardian Contact Info */}
                        <div className="grid grid-cols-2 gap-3 mt-2" data-id="element-2197">
                          {displayStudent.guardian.email && <div className="space-y-1" data-id="element-2198">
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2199">
                                Email
                              </span>
                              <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2200">
                                {displayStudent.guardian.email}
                              </p>
                            </div>}
                          {displayStudent.guardian.phone && <div className="space-y-1" data-id="element-2201">
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2202">
                                Phone
                              </span>
                              <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2203">
                                {formatPhoneNumber(displayStudent.guardian.phone)}
                              </p>
                            </div>}
                        </div>
                        {displayStudent.guardian.preferredContact && <div className="mt-2" data-id="element-2204">
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2205">
                              Preferred Contact:{' '}
                            </span>
                            <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2206">
                              {displayStudent.guardian.preferredContact}
                            </span>
                          </div>}
                      </div> : <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2207">
                        No guardian information provided
                      </p>}
                  </div>}
              </div>
              {/* Notes Section */}
              <div data-id="element-2208">
                <div className="mb-3" data-id="element-2209">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2210">
                    Notes
                  </h3>
                </div>
                {isEditMode ? <div className="space-y-3" data-id="element-2211">
                    <textarea value={editedNotes} onChange={e => setEditedNotes(e.target.value)} rows={4} className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-emerald-500/50`} placeholder="Add notes about this student..." data-id="element-2212" />
                  </div> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-2213">
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2214">
                      {displayStudent.notes || 'No notes for this student.'}
                    </p>
                  </div>}
              </div>
            </div>
            {/* Footer Actions */}
            <div className={`px-6 py-4 border-t ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} flex justify-between`} data-id="element-2215">
              {isEditMode ? <div className="flex gap-2 ml-auto" data-id="element-2216">
                  <button onClick={handleCancelEdit} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${darkMode ? 'border-gray-400 text-gray-400 hover:bg-gray-400/10' : 'border-gray-500 text-gray-500 hover:bg-gray-50'}`} data-id="element-2217">
                    <XIcon className="h-4 w-4" data-id="element-2218" />
                    Cancel
                  </button>
                  <button onClick={handleSaveAll} disabled={validationErrors.email !== null || validationErrors.phone !== null || validationErrors.guardianEmail !== null || validationErrors.guardianPhone !== null} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${validationErrors.email !== null || validationErrors.phone !== null || validationErrors.guardianEmail !== null || validationErrors.guardianPhone !== null ? 'opacity-50 cursor-not-allowed border-gray-400 text-gray-400' : darkMode ? 'border-emerald-400 text-emerald-400 hover:bg-emerald-400/10' : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'}`} data-id="element-2219">
                    <SaveIcon className="h-4 w-4" data-id="element-2220" />
                    Save
                  </button>
                </div> : <>
                  <button onClick={handleDeleteClick} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${darkMode ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : 'border-red-500 text-red-600 hover:bg-red-50'}`} data-id="element-2221">
                    <Trash2Icon className="h-4 w-4" data-id="element-2222" />
                    Delete
                  </button>
                  <button onClick={handleEnterEditMode} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${darkMode ? 'border-emerald-400 text-emerald-400 hover:bg-emerald-400/10' : 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'}`} data-id="element-2223">
                    <PencilIcon className="h-4 w-4" data-id="element-2224" />
                    Edit Profile
                  </button>
                </>}
            </div>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-2225">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={!isDeleting ? handleDeleteCancel : undefined} data-id="element-2226" />
          {/* Modal */}
          <div className={`relative w-full max-w-md transform transition-all ${darkMode ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50' : 'bg-white border border-gray-200'} shadow-2xl rounded-2xl overflow-hidden`} data-id="element-2227">
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b ${darkMode ? 'border-navy-700/50' : 'border-gray-200'}`} data-id="element-2228">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2229">
                Delete Student Profile
              </h3>
            </div>
            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4" data-id="element-2230">
              <div className={`flex items-start gap-3 p-3 rounded-lg ${darkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`} data-id="element-2231">
                <AlertCircleIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2232" />
                <div data-id="element-2233">
                  <p className={`text-sm font-medium ${darkMode ? 'text-red-300' : 'text-red-800'}`} data-id="element-2234">
                    This action cannot be undone
                  </p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-red-200/80' : 'text-red-700'}`} data-id="element-2235">
                    All student data, including lessons, notes, and progress
                    will be permanently deleted.
                  </p>
                </div>
              </div>
              <div data-id="element-2236">
                <label htmlFor="delete-password" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2237">
                  Enter your password to confirm
                </label>
                <input id="delete-password" type="password" value={deletePassword} onChange={e => {
              setDeletePassword(e.target.value);
              setPasswordError('');
            }} onKeyDown={handlePasswordKeyDown} placeholder="Your password" disabled={isDeleting} className={`w-full px-4 py-2 rounded-lg border transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-500 focus:border-red-500 focus:ring-1 focus:ring-red-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-red-500 focus:ring-1 focus:ring-red-500'} ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-2238" />
                {passwordError && <p className={`mt-2 text-sm flex items-center gap-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-2239">
                    <AlertCircleIcon className="h-4 w-4" data-id="element-2240" />
                    {passwordError}
                  </p>}
              </div>
            </div>
            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} flex justify-end gap-3`} data-id="element-2241">
              <button onClick={handleDeleteCancel} disabled={isDeleting} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-2242">
                Cancel
              </button>
              <button onClick={handleDeleteConfirm} disabled={isDeleting || !deletePassword.trim()} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'} ${isDeleting || !deletePassword.trim() ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-2243">
                {isDeleting ? <span className="inline-flex items-center" data-id="element-2244">
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-2245">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2246"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-2247"></path>
                    </svg>
                    Deleting...
                  </span> : 'Delete Student'}
              </button>
            </div>
          </div>
        </div>}
    </div>;
};